// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';
import moment from 'moment';
import Switch from 'react-switch';

import Select2, { DataSelect2, EventSelect2 } from 'wdna-select2';
import DatePicker, { DateSelectorConfig } from 'wdna-date-selector';

import { UserDO } from 'wdna-components/lib/types.new';
import UiValidate from 'wdna-components/lib/forms/validation/UiValidate';
import { messageError } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';
import { initProcess, finishProcess } from 'wdna-components/lib/scheduler/actions';
import { hasLevel } from 'wdna-components/lib/helpers/permission-new';

import { ContextType, FilterContext } from '../../../../types';
import { typeSensorVariable, TypeSensorVariableDO, typeSensor, TypeSensorDO } from '../../../configuration/requests';
import { company, CompanyDO } from '../../../management/requests';
import { sensor, SensorDO } from '../../requests';

interface Props {
  rowId?: number;
}

interface State {
  companyArray: CompanyDO[];
  typeSensorArray: TypeSensorDO[];
  sensorArray: SensorDO[];
  typeVariableArray: TypeSensorVariableDO[];

  dates: Date[];
  flatpickrMode: string;
  groupDateTime: string;

  dateIni: string;
  timeIni: string;
  dateEnd: string;
  timeEnd: string;
  groupTime: string;

  multiple: boolean;
  variables: any[];
  checked: boolean;
  autoRefresh: boolean;

  sensorId?: number;
  companyId?: number;
  typeSensorId?: number;
}

export default class Filter extends React.Component<Props, State> {
  private pid: string;
  private mounted: boolean;
  private userIsURW: boolean;

  private timer: NodeJS.Timeout | undefined;

  public static contextType: React.Context<ContextType> = FilterContext;
  public context: React.ContextType<typeof FilterContext> | undefined;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;
    this.userIsURW = false;

    const dIni: Date = new Date();
    dIni.setDate(dIni.getDate() - 1);
    const dEnd: Date = new Date();

    this.state = {
      companyArray: [],
      typeSensorArray: [],
      sensorArray: [],
      typeVariableArray: [],

      dates: this.initialDates(),
      flatpickrMode: 'range',
      groupDateTime: '60m',

      dateIni: moment(dIni).add(1, 'days').format('YYYY-MM-DD'),
      timeIni: '00:00',
      dateEnd: moment(dEnd).add(1, 'days').format('YYYY-MM-DD'),
      timeEnd: '00:00',
      groupTime: '0',

      multiple: true,
      variables: [],
      checked: false,
      autoRefresh: false,
    };
  }

  public componentDidMount(): void {
    this.mounted = true;
    this.userIsURW = hasLevel('role', ['ADM', 'URW']);

    this.load();
  }

  public componentDidUpdate(): void {
    /* pass */
  }

  public componentWillUnmount(): void {
    this.mounted = false;
    this.refresh(false);
  }

  public setLocalState = (nextState: Partial<State>, callback?: (() => void)): void => {
    if (this.mounted) {
      this.setState((prevState: State) => ({
        ...prevState,
        ...nextState,
      }), callback);
    }
  };

  public load = (): void => {
    if (_.isNil(this.props.rowId)) {
      this.loadFilter().catch(() => { /* pass */ });
    } else {
      this.loadVariables().catch(() => { /* pass */ });
    }
  };

  public initialDates = (): Date[] => {
    const today: Date = moment().add(1, 'days').toDate();
    const twoDaysBefore: Date = moment().toDate();
    return [twoDaysBefore, today];
  };

  public hideSidebar = (): void => {
    $('#right-panel').addClass('hidden-functions');
    $('#tab-main-content').removeClass('tab-left-content');
    $('#toggle-func-btn').addClass('transparent sidebar-hidden');
  };

  public loadFilter = async (): Promise<void> => {
    const companyFilter: Record<string, any> = {};
    if (!this.userIsURW) {
      const user: UserDO = _.get(window.store.getState(), ['auth', 'user']);
      companyFilter.id = _.defaultTo(_.toNumber(user.access?.companyId), undefined);
      companyFilter.clientId = _.defaultTo(_.toNumber(user.access?.clientId), undefined);
    }

    this.loadingStart();
    try {
      const [typeSensorArray, companyArray] = await Promise.all([
        typeSensor<TypeSensorDO[]>('list', undefined, { active: true }, { params: { order: 'typeModule' } }) || [],
        company<CompanyDO[]>('list', undefined, companyFilter) || [],
      ]);
      if (this.userIsURW) (companyArray as any[]).unshift({ id: 0, name: 'ALL' });
      this.setLocalState({
        typeSensorArray,
        companyArray,
      });
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public loadVariables = async (): Promise<void> => {
    let { typeSensorId } = this.state;

    this.loadingStart();
    try {
      if (!_.isFinite(typeSensorId)) {
        const idSensor: number | undefined = this.state.sensorId || this.props.rowId;
        const sensorInfo: SensorDO | undefined = await sensor<SensorDO>('get', String(idSensor));
        typeSensorId = sensorInfo?.typeSensorId;
      }
      const typeVariableArray: TypeSensorVariableDO[] = await typeSensorVariable<TypeSensorVariableDO[]>('list', undefined, { typeSensorId }) || [];
      this.setLocalState({ typeVariableArray });
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public loadSensors = async (): Promise<void> => {
    const { companyId, typeSensorId } = this.state;
    if (!this.userIsURW && !_.isFinite(companyId)) return;

    const request: Record<string, any> = {};
    if (companyId) request.companyId = companyId;
    if (typeSensorId) request.typeSensorId = typeSensorId;

    this.loadingStart();
    try {
      const sensorArray: SensorDO[] = await sensor<SensorDO[]>('list', undefined, request) || [];
      this.setLocalState({
        sensorId: undefined,
        sensorArray,
      });
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public submit = async (): Promise<void> => {
    if (!this.mounted) return;
    const { sensorId, dateIni, timeIni, dateEnd, timeEnd, groupTime, variables } = this.state;

    const idSensor: number | undefined = sensorId || this.props.rowId;
    if (!idSensor) { messageError('Error', 'Please select a device'); return; }

    const dIni: string | undefined = (dateIni) ? moment(`${dateIni}T${timeIni || '00'}:00`, 'YYYY-MM-DDThh:mm').toDate().toISOString() : undefined;
    const dEnd: string | undefined = (dateEnd) ? moment(`${dateEnd}T${timeEnd || '00'}:00`, 'YYYY-MM-DDThh:mm').toDate().toISOString() : undefined;

    // REQUEST: register_graph
    const req: Record<string, any> = {
      group: groupTime,
      values: variables,
      round: false,
      from: dIni,
      to: dEnd,
    };

    // TODO: Special case for type sensor 4 ****************************
    const sensorInfo: SensorDO | undefined = await sensor<SensorDO>('get', String(idSensor));
    const typeSensorId: number | undefined = sensorInfo?.typeSensorId;
    if (typeSensorId === 4) {
      const elem: string = '4';
      if (req.values.includes(elem)) {
        const dim: number = this.state.variables.length;
        const pos: number = req.values.indexOf(elem);
        req.operators = [];
        req.operators = Array.from({ length: dim }, () => 'mean');
        req.operators[pos] = 'sum';
      }
    }

    if (this.isValidRequest(req)) {
      this.loadingStart();
      try {
        const data: any = await sensor('register_graph', String(idSensor), req);
        this.updData(data);
      } catch (error) {
        messageError('Error', getErrorMessage(error));
      }
      this.loadingFinish();
      this.hideSidebar();
    }
  };

  public isValidRequest = (data: Record<string, any>): boolean => {
    const { from, to, group, values } = data;
    if (!from || !to) {
      messageError('Error', 'Please select valid dates for the interval');
      return false;
    }
    if (!group) {
      messageError('Error', 'Please select a time interval');
      return false;
    }
    if (_.isEmpty(values)) {
      messageError('Error', 'Please select at least a variable to plot');
      return false;
    }
    return true;
  };

  private toTimestamp = (strDate: string): number => {
    const datum: number = Date.parse(strDate);
    return datum / 1000;
  };

  public updData = (reqdata: any): void => {
    const { typeVariableArray, multiple } = this.state;

    let odd: boolean = true;
    const parsedData: any[] = reqdata.map((e: any) => {
      const serieData: any = e.data
        .map((point: any) => {
          const dateTimeStamp: any = point[0];
          const intervalTimeArray: any = [this.toTimestamp(dateTimeStamp) * 1000, point[1]];
          return intervalTimeArray;
        })
        .sort((a: any, b: any) => a[0] - b[0]);

      const regex: RegExp = /(\d+)/g;
      const typeVariable: any = typeVariableArray[parseInt(e.name.match(regex), 10)];

      const yAxis: number = odd ? 0 : 1;
      odd = multiple || !odd;

      return {
        type: 'line',
        yAxis,
        zIndex: 2,
        data: serieData,
        name: `${e.name} - ${typeVariable.variableName} [${typeVariable.variableUnit}]`,
      };
    });

    // TODO: Condition Pressure
    if (parsedData !== undefined) {
      for (let i: number = 0; i < parsedData.length; i++) {
        if (parsedData[i].name === 'D05 - Pressure [bars]') {
          parsedData[i].name = 'D05 - Pressure [hPa]';
          for (let j: number = 0; j < parsedData[i].data.length; j++) {
            parsedData[i].data[j][1] *= 1000;
          }
        }
      }
    }

    this.context?.updateParent({
      update: window.getStr(),
      multiple: this.state.multiple,
      graphInput: {
        data: parsedData,
      },
    });
  };

  public loadingStart = (): void => {
    this.context?.updateParent({ loading: true, });
    window.store.dispatch(initProcess(this.pid));
  };

  public loadingFinish = (): void => {
    this.context?.updateParent({ loading: false, });
    window.store.dispatch(finishProcess(this.pid));
  };

  /* Handlers */
  public handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
    const { name, value, checked, type, dataset } = (e as React.ChangeEvent<HTMLInputElement>).target;
    const { ctype } = dataset || {};
    // Process new value from ctype
    let newValue: any = (type === 'checkbox') ? !!checked : value;
    if (ctype === 'number') newValue = (/^\d+(\.\d+){0,1}$/.test(value)) ? _.toNumber(value) : null;
    // Create new data state structure
    const levels: string[] = name.split('.');
    const nextState: Record<string, any> = { [levels[0]]: { ...this.state[levels[0]] } };
    _.set(nextState, levels, newValue);
    this.setLocalState(nextState);
  };

  public handleChangeFilter = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
    this.handleChange(e);
    this.loadSensors().catch(() => { /* pass */ });
  };

  public handleChangeDevice = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
    this.handleChange(e);
    this.loadVariables().catch(() => { /* pass */ });
  };

  public handleChangeDatePicker = (config: DateSelectorConfig): void => {
    const { flatpickrMode } = this.state;

    const hours: string[] = config.getHours() as string[];
    const dates: Date[] = config.getDates() as Date[];
    const newDataIni: moment.Moment = moment(dates[0]);
    const newDataEnd: moment.Moment = (flatpickrMode === 'range') ? moment(dates[1]) : moment(newDataIni).add(1, 'days');

    this.setLocalState({
      dates,
      timeIni: hours[0],
      timeEnd: hours[1],
      dateIni: newDataIni.format('YYYY-MM-DD'),
      dateEnd: newDataEnd.format('YYYY-MM-DD'),
      flatpickrMode: config.getMode(),
    });
  };

  public handleChangeMultiple = (e: EventSelect2): void => {
    const { id, selected } = e.params.data;
    if (selected) {
      this.addValue(id);
    } else {
      this.remValue(id);
    }
  };

  /* Management values of multiple select */
  public isValueSelected = (id: string): boolean => {
    const { variables } = this.state || {};
    if (!variables) return false;
    return variables.includes(id);
  };

  public addValue = (id: string): void => {
    if (this.isValueSelected(id)) return;
    const { variables } = this.state || {};
    const selection: any[] = !variables ? [] : variables.slice();
    selection.push(id);
    this.setLocalState({
      variables: selection.sort((a: any, b: any) => {
        return a - b;
      }),
    });
  };

  public remValue = (id: string): void => {
    if (!this.isValueSelected(id)) return;
    const { variables } = this.state || {};
    if (!variables) return;
    const selection: any[] = variables.filter((itemId: any) => itemId !== id);
    this.setLocalState({ variables: selection });
  };

  public handleSubmit = (): void => {
    this.submit().catch(() => { /* pass */ });
  };

  public refresh(create: boolean): void {
    if (create) {
      this.timer = setInterval(() => { this.handleSubmit(); }, 60000);
    } else {
      clearInterval(this.timer);
    }
  }

  public handleAutoRefresh = (autoRefresh: boolean): void => {
    this.setLocalState({
      autoRefresh,
    }, () => { this.refresh(autoRefresh); });
  };

  public render(): JSX.Element {
    const { companyArray, typeSensorArray, sensorArray, typeVariableArray, variables } = this.state;
    const { sensorId, companyId, typeSensorId, dates, flatpickrMode, groupDateTime, multiple, groupTime, autoRefresh } = this.state;

    const user: UserDO = _.get(window.store.getState(), ['auth', 'user']);
    const showMainFilter: boolean = (this.props.rowId == null);

    return (
      <div className="main-filters">
        <div className="auto-refresh-button">
          <br />
          <label htmlFor="normal-switch" style={{ marginRight: '20px' }}>
            <Switch
              id="auto-refresh-button"
              className="react-switch"
              aria-labelledby="neat-label"
              height={30}
              onColor="#78a329"
              offColor="#5c6875"
              offHandleColor="#444"
              onHandleColor="#b7f93d"
              checked={autoRefresh}
              onChange={this.handleAutoRefresh}
            />
            <span style={{ marginLeft: '10px' }}>Auto Refresh</span>
          </label>
        </div>
        { /* TODO: REVIEW !!!!! */ }
        {showMainFilter && (
          <div className="sideFilter" style={{ marginBottom: '15px' }}>
            <Select2
              name="companyId"
              value={companyId}
              onChange={this.handleChangeFilter}
              data-ctype="number"
              data={companyArray
                .filter((comp: CompanyDO): boolean => {
                  const a: boolean = this.userIsURW || !_.isEmpty(comp.name);
                  if (user.access?.companyId === undefined) {
                    companyArray.map((item: CompanyDO): DataSelect2 => {
                      return { title: item.name, text: item.name, id: item.id };
                    });
                  }
                  return a;
                })
                .map((item: CompanyDO): DataSelect2 => {
                  return {
                    id: item.id,
                    text: item.name,
                    title: item.name,
                  };
                })}
              options={{ placeholder: 'Select Company' }}
              style={{ width: '100%' }}
            />
          </div>
        )}
        {showMainFilter && (
          <div className="sideFilter" style={{ marginBottom: '15px' }}>
            <Select2
              name="typeSensorId"
              value={typeSensorId}
              onChange={this.handleChangeFilter}
              data-ctype="number"
              data={typeSensorArray.map((item: TypeSensorDO): DataSelect2 => {
                return {
                  id: item.id,
                  text: `${item.name}`,
                  title: `${item.name} [${item.code}]`,
                };
              })}
              options={{ placeholder: 'Select Sensor Type' }}
              style={{ width: '100%' }}
            />
          </div>
        )}
        {showMainFilter && (
          <div className="sideFilter" style={{ marginBottom: '15px' }}>
            <Select2
              name="sensorId"
              value={sensorId}
              onChange={this.handleChangeDevice}
              data-ctype="number"
              data={sensorArray.map((item: SensorDO): DataSelect2 => {
                return {
                  id: item.id,
                  text: `${item.description}`,
                  title: `${item.code} - ${item.name}`,
                };
              })}
              options={{ placeholder: 'Select Sensor To Show' }}
              style={{ width: '100%' }}
            />
          </div>
        )}
        <UiValidate>
          <div className="sideFilter" style={{ marginBottom: '15px' }}>
            <Select2
              multiple
              name="variables"
              value={variables}
              onSelect={this.handleChangeMultiple}
              onUnselect={this.handleChangeMultiple}
              data={typeVariableArray.map((value: TypeSensorVariableDO, idx: number): DataSelect2 => {
                return {
                  id: idx,
                  text: `${value.variableName}`,
                  title: `${value.variableName} [D${idx}] [${value.variableUnit}]`,
                };
              })}
              options={{ placeholder: 'Select Variables' }}
              style={{ width: '99%' }}
              required
            />
          </div>
          <div className="sideFilter" style={{ marginBottom: '15px' }}>
            <label className="label">Multiple</label>
            <input
              id="multiple"
              name="multiple"
              type="checkbox"
              checked={multiple || false}
              onChange={this.handleChange}
              data-ctype="boolean"
              className="input-new"
              style={{ marginLeft: '20px', marginRight: '0px' }}
            />
          </div>
          <DatePicker
            icon
            hours={[]}
            dates={dates}
            showChangeModes
            mode={flatpickrMode}
            granularity={groupDateTime}
            iconClass="wdna-ico-icon-calendar"
            handleChanges={this.handleChangeDatePicker}
          />
          <div className="sideFilter" style={{ marginBottom: '15px' }}>
            <div>
              <Select2
                name="groupTime"
                value={groupTime}
                onChange={this.handleChange}
                data={[
                  { id: 0, value: 0, text: 'Raw Data' },
                  { id: 1, value: 15, text: '15 Minutes' },
                  { id: 2, value: 30, text: '30 Minutes' },
                  { id: 3, value: 60, text: '1 Hour' },
                  { id: 4, value: 1440, text: 'Day' },
                  { id: 5, value: 43200, text: 'Month' },
                ]}
                options={{ placeholder: 'Select Time Interval' }}
                style={{ width: '99%' }}
                required
              />
            </div>
          </div>
          <div style={{ marginTop: '1px' }}>
            <button type="button" className="filter-btn btn btn-default" onClick={(): void => { this.submit().catch(() => { /* pass */ }); }}>
              <i className="wdna-ico-icon-playsearch" />
              <span>GO</span>
            </button>
          </div>
        </UiValidate>
      </div>
    );
  }
}
